import { useState, useEffect } from "react";
import AOS from 'aos';
import 'aos/dist/aos.css';
import './ViewCart.css';
import { useSelector, useDispatch } from "react-redux";
import { removeProductFromCart, updateCartItemQuantity, clearUserCart } from "../redux/cartSlice";
import { toast } from 'react-hot-toast';
import { Link } from "react-router-dom";
import { MdRemoveCircle } from "react-icons/md";
import { IoIosAddCircle } from "react-icons/io";
import { Modal, Button } from "react-bootstrap";
import ProductModal from '../components/ProductModal';
import styles from '../components/Card.module.css';
import { getProductById } from '../api';

const ViewCart = () => {
    const cart = useSelector(state => state.cart);
    const dispatch = useDispatch();
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [itemToRemove, setItemToRemove] = useState(null);
    // store fetched product details keyed by product id
    const [productDetails, setProductDetails] = useState({});

    useEffect(() => {
        AOS.init({ duration: 1000, once: true });
    }, []);

    // Fetch and cache product details for items in the cart so productDetails is defined
    useEffect(() => {
        if (!cart?.items || cart.items.length === 0) {
            setProductDetails({});
            return;
        }
        let cancelled = false;
        const fetchDetails = async () => {
            const results = {};
            await Promise.all(cart.items.map(async (item) => {
                try {
                    const res = await getProductById(item.id);
                    if (res && res.data) {
                        results[item.id] = res.data;
                    }
                } catch (err) {
                    console.error('Failed to fetch product details for', item.id, err);
                }
            }));
            if (!cancelled) {
                setProductDetails(results);
            }
        };
        fetchDetails();
        return () => { cancelled = true; };
    }, [cart?.items]);

    const handleQuantityChange = (product, newQuantity) => {
        const currentItem = cart.items.find(item => item.id === product.id && item.selectedColor === product.selectedColor && item.selectedSize === product.selectedSize);
        if (!currentItem) return; // Safety check

        if (newQuantity < 1) {
            dispatch(removeProductFromCart(currentItem)); 
        } else {
            dispatch(updateCartItemQuantity(currentItem, newQuantity)); // Pass the full item
        }
    };

    const [isToastVisible, setIsToastVisible] = useState(false);

    const handleRemoveItem = (item) => {
        setItemToRemove(item);
        setShowConfirm(true);
    };

    const handleConfirmRemove = async () => {
        if (itemToRemove) {
            try {
                await dispatch(removeProductFromCart(itemToRemove));
                toast.success('Item removed from cart.');
            } catch (err) {
                console.error(err);
                toast.error('Failed to remove item.');
            }
        }
        setShowConfirm(false);
        setItemToRemove(null);
    };

    const handleClearCart = () => {
        if (isToastVisible) return;

        setIsToastVisible(true);
        toast((t) => (
            <div style={{ padding: '8px 12px' }}>
                <div style={{ marginBottom: 8, fontWeight: 600 }}>Are you sure you want to clear the cart?</div>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        onClick={async () => {
                            try {
                                await dispatch(clearUserCart());
                                toast.dismiss(t.id);
                                setIsToastVisible(false);
                            } catch (err) {
                                console.error(err);
                                toast.error('Failed to clear cart.');
                                toast.dismiss(t.id);
                                setIsToastVisible(false);
                            }
                        }}
                        style={{ background: '#82278A', color: '#fff', border: 'none', padding: '6px 10px', borderRadius: 4 }}
                    >Yes</button>
                    <button onClick={() => {
                        toast.dismiss(t.id);
                        setIsToastVisible(false);
                    }} style={{ background: '#eee', border: 'none', padding: '6px 10px', borderRadius: 4 }}>No</button>
                </div>
            </div>
        ), { 
            duration: Infinity,
            onDismiss: () => setIsToastVisible(false)
        });
    };

    const cartItems = cart?.items || [];

    // Helper function to safely format price (more robust)
    const formatPrice = (price) => {
        const numPrice = Number(price);
        return isNaN(numPrice) ? '₹0.00' : `₹${numPrice.toFixed(2)}`;
    };

    // Helper function to safely calculate subtotal (more robust)
    const calculateSubtotal = (price, quantity) => {
        const numPrice = Number(price);
        const numQuantity = Number(quantity);
        return (isNaN(numPrice) || isNaN(numQuantity)) ? 0 : numPrice * numQuantity;
    };

    // Use totals directly from Redux state, which are now calculated safely
    const displayTotal = cart.total || 0;
    const displayItemCount = cart.itemCount || 0;
    const tax = displayTotal * 0.1;
    const totalWithTax = displayTotal + tax;


    if (cartItems.length === 0) {
        return (
            <div className="container my-5" data-aos="fade-up">
                <div className="row">
                    <div className="col-12 text-center">
                        <h2 data-aos="fade-down">Your Cart is Empty</h2>
                        <p data-aos="fade-up" data-aos-delay="200">Looks like you haven't added anything yet.</p>
                        <Link to="/products" className="btn btn-outline-success scale-btn" data-aos="zoom-in" data-aos-delay="400">
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container my-5" data-aos="fade-up">
            <div className="row">
                <div className="col-12">
                    <div className="d-flex justify-content-between align-items-center mb-4" data-aos="fade-right">
                        <h2>Shopping Cart</h2>
                        <button className="btn btn-outline-danger scale-btn" onClick={handleClearCart} data-aos="fade-left" >
                            Clear Cart
                        </button>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
                    <div className="card" data-aos="zoom-in">
                        <div className="card-body">
                            {cartItems.map((item, index) => (
                                <div key={item.id || index} className="row mb-4 border-bottom pb-3" data-aos="fade-up" data-aos-delay={index * 200}>
                                    <div className="col-md-3">
                                        <img src={item.image} alt={item.title || 'Product Image'} className="img-fluid rounded" style={{ height: "120px", objectFit: "contain", cursor: "pointer" }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }} />
                                    </div>
                                    <div className="col-md-5">
                                        <h5 className="card-title mt-4 pt-4" style={{ cursor: "pointer" }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }}>{item.title || 'Product'}</h5>
                                        
                                        {/* Selected Color and Size Display - Added in column layout */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                                            {item.selectedColor && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <strong style={{ fontSize: "0.9rem", color: "#666", minWidth: '60px' }}>Color:</strong>
                                                    <span style={{
                                                        fontSize: "0.9rem",
                                                        color: "#333",
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #dee2e6'
                                                    }}>
                                                        {item.selectedColor}
                                                    </span>
                                                </div>
                                            )}
                                            {item.selectedSize && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <strong style={{ fontSize: "0.9rem", color: "#666", minWidth: '60px' }}>Size:</strong>
                                                    <span style={{
                                                        fontSize: "0.9rem",
                                                        color: "#333",
                                                        backgroundColor: '#f8f9fa',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #dee2e6'
                                                    }}>
                                                        {item.selectedSize}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        <p className="card-text text-muted" style={{ fontSize: "0.9rem", cursor: "pointer" }} onClick={async () => {
                                            try {
                                                const fullProduct = await getProductById(item.id);
                                                setSelectedProduct(fullProduct.data);
                                                setModalOpen(true);
                                            } catch (error) {
                                                console.error('Failed to fetch product details:', error);
                                            }
                                        }}>
                                            {/* {item.description ? `${item.description.split(" ").slice(0, 15).join(" ")}...` : "No description available."} */}
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            {productDetails[item.id] && productDetails[item.id].variants && (
                                                <>
                                                    {productDetails[item.id].variants.some(v => v.color) && (
                                                        <div>
                                                            <strong style={{ fontSize: "0.8rem", color: "#666" }}>Available Colors:</strong>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px' }}>
                                                                {[...new Set(productDetails[item.id].variants.map(v => v.color).filter(Boolean))].map(color => (
                                                                    <span key={color} style={{
                                                                        fontSize: "0.7rem",
                                                                        color: "#666",
                                                                        // backgroundColor: item.selectedColor === color ? '#e0e0e0' : 'transparent',
                                                                        padding: '2px 5px',
                                                                        borderRadius: '3px',
                                                                        // border: item.selectedColor === color ? '1px solid #666' : 'none'
                                                                    }}>
                                                                        {color}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {productDetails[item.id].variants.some(v => v.size) && (
                                                        <div>
                                                            <strong style={{ fontSize: "0.8rem", color: "#666" }}>Available Sizes:</strong>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '5px' }}>
                                                                {[...new Set(productDetails[item.id].variants.map(v => v.size).filter(Boolean))].map(size => (
                                                                    <span key={size} style={{
                                                                        fontSize: "0.7rem",
                                                                        color: "#666",
                                                                        backgroundColor: item.selectedSize === size ? '#e0e0e0' : 'transparent',
                                                                        padding: '2px 5px',
                                                                        borderRadius: '3px',
                                                                        border: item.selectedSize === size ? '1px solid #666' : 'none'
                                                                    }}>
                                                                        {size}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <span className="h5 mb-0">{formatPrice(item.price)}</span>
                                            <button className="btn btn-sm btn-outline-danger scale-btn" onClick={() => handleRemoveItem(item)}>
                                                Remove
                                            </button>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <span className="me-2" style={{ marginBottom: "20px" }}>Quantity:</span>
                                            <div className={styles.quantityContainer}>
                                                <button className={styles.quantityButton} onClick={() => handleQuantityChange(item, (item.quantity || 1) - 1)} style={{ marginBottom: "20px" }}>
                                                    <MdRemoveCircle />
                                                </button>
                                                {/* Ensure quantity displayed is at least 1 if item exists */}
                                                <span className={styles.quantity} style={{ marginBottom: "20px" }}>{item.quantity || 1}</span>
                                                <button className={styles.quantityButton} onClick={() => handleQuantityChange(item, (item.quantity || 0) + 1)} style={{ marginBottom: "20px" }}>
                                                    <IoIosAddCircle />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="mt-2">
                                            <strong>Subtotal: {formatPrice(calculateSubtotal(item.price, item.quantity))}</strong>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="col-lg-4" data-aos="fade-up" data-aos-delay="200">
                    <div className="card" data-aos="zoom-in" data-aos-delay="300">
                        <div className="card-body">
                            <h5 className="card-title">Order Summary</h5>
                            <div className="d-flex justify-content-between mb-2">
                                
                                <span>Items ({displayItemCount})</span>
                                <span>{formatPrice(displayTotal)}</span>
                                </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Shipping</span>
                                <span>₹0.00</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Tax (10%)</span>
                                <span>{formatPrice(tax)}</span>
                            </div>
                            <hr />
                            <div className="d-flex justify-content-between mb-3">
                                <strong>Total</strong>
                                <strong>{formatPrice(totalWithTax)}</strong>
                            </div>
                            <Link to="/checkout" className="btn btn-primary w-100 mb-2 scale-btn" style={{ backgroundColor: "#82278A", borderColor: "#82278A" }} data-aos="zoom-in" data-aos-delay="400">
                                Proceed to Checkout
                            </Link>
                            <Link to="/products" className="btn btn-outline-secondary w-100 scale-btn" data-aos="zoom-in" data-aos-delay="500">
                                Continue Shopping
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Body style={{ textAlign: 'center', padding: '50px' }}>
                    <p style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>Are you sure you want to remove the item?</p>
                    <Button variant="danger" onClick={handleConfirmRemove} className="scale-btn" style={{backgroundColor:'rgb(130, 39, 138)'}}>Yes</Button>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)} className="scale-btn" style={{ marginLeft: '10px' }}>No</Button>
                </Modal.Body>
            </Modal>
            <ProductModal post={selectedProduct} isOpen={modalOpen} onClose={() => setModalOpen(false)} initialSelectedColor={selectedProduct?.selectedColor} initialSelectedSize={selectedProduct?.selectedSize} isWishlistPage={false} />

            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Removal</Modal.Title>
                </Modal.Header>
                <Modal.Body>Are you sure you want to remove this item from the cart?</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirm(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmRemove}>
                        Remove
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ViewCart;